import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Modal, Button, TextArea, Typography } from '@anchor/react-components/dist/lib/components';
import { toast } from 'react-toastify';
import { TranscriptService } from '../../services/transcriptService';
import { FreeTextService } from '../../services/freeTextService';
import { useMsal } from '@azure/msal-react';
import './ArtifactsPanel.css';

// ─── TranscriptSection ─────────────────────────────────────────────────────────
const TranscriptSection = ({ postMortemId, incidentId, uploadedBy, transcriptType, isExpanded, onStatusChange }) => {
  const { accounts } = useMsal();
  const {
    checkTranscriptExists, uploadTranscript, deleteTranscript,
    checkPirTranscriptExists, uploadPirTranscript, deletePirTranscript,
    checkSreReviewTranscriptExists, uploadSreReviewTranscript, deleteSreReviewTranscript,
  } = TranscriptService();

  const TRANSCRIPT_CONFIG = {
    transcript: {
      checkExists: checkTranscriptExists,
      upload: uploadTranscript,
      delete: deleteTranscript,
    },
    pir: {
      checkExists: checkPirTranscriptExists,
      upload: uploadPirTranscript,
      delete: deletePirTranscript,
    },
    'sre-review': {
      checkExists: checkSreReviewTranscriptExists,
      upload: uploadSreReviewTranscript,
      delete: deleteSreReviewTranscript,
    },
  };

  const config = TRANSCRIPT_CONFIG[transcriptType];
  const [transcripts, setTranscripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isExpanded) {
      loadTranscripts();
    }
  }, [isExpanded, postMortemId]);

  const loadTranscripts = async () => {
    try {
      setLoading(true);
      const data = await config.checkExists(postMortemId);
      setTranscripts(data?.hasTranscript && data.transcripts ? data.transcripts : []);
    } catch {
      toast.error('Failed to load transcripts');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const userEmail = uploadedBy || accounts[0]?.username || 'unknown';
    let successCount = 0;
    setUploading(true);

    for (const file of files) {
      if (!file.name.toLowerCase().endsWith('.docx')) {
        toast.error(`${file.name}: Only .docx files are supported`, { autoClose: 3000 });
        continue;
      }
      if (file.size > 500 * 1024 * 1024) {
        toast.error(`${file.name}: File size exceeds 500MB limit`, { autoClose: 3000 });
        continue;
      }
      if (transcripts.some(t => t.fileName === file.name)) {
        toast.error(`${file.name}: Already uploaded`, { autoClose: 3000 });
        continue;
      }
      try {
        await config.upload(postMortemId, incidentId, file, userEmail);
        successCount++;
      } catch (e) {
        toast.error(`${file.name}: ${e.response?.data || 'Upload failed'}`, { autoClose: 3000 });
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} file(s) uploaded successfully`);
      onStatusChange?.(transcriptType, true);
    }
    await loadTranscripts();
    event.target.value = '';
    setUploading(false);
  };

  const handleDelete = async (fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) return;
    try {
      await config.delete(postMortemId, fileName);
      toast.success('Transcript deleted');
      const remaining = transcripts.filter(t => t.fileName !== fileName);
      setTranscripts(remaining);
      if (remaining.length === 0) onStatusChange?.(transcriptType, false);
    } catch (e) {
      toast.error(e.response?.data || 'Failed to delete transcript');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : '');

  const inputId = `artifacts-file-${transcriptType}-${postMortemId}`;

  return (
    <div className="artifact-section-body">
      <div className="artifact-upload-row">
        <input
          type="file"
          id={inputId}
          accept=".docx"
          multiple
          onChange={handleFileUpload}
          disabled={uploading}
          style={{ display: 'none' }}
        />
        <Button
          label={uploading ? 'Uploading...' : '+ Upload .docx'}
          appearance="alt"
          fit="small"
          disabled={uploading}
          onClick={(e) => { e.preventDefault(); document.getElementById(inputId).click(); }}
        />
        <span className="artifact-upload-hint">Only .docx files · max 500 MB</span>
      </div>

      {loading ? (
        <div className="artifact-list-placeholder">Loading...</div>
      ) : transcripts.length === 0 ? (
        <div className="artifact-list-placeholder">No files uploaded yet</div>
      ) : (
        <ul className="artifact-file-list">
          {transcripts.map((t, i) => (
            <li key={i} className="artifact-file-item">
              <span className="artifact-file-icon">📄</span>
              <div className="artifact-file-info">
                <span className="artifact-file-name" title={t.fileName}>{t.fileName}</span>
                <span className="artifact-file-meta">
                  {formatFileSize(t.fileSize)} &bull; {formatDate(t.uploadedAt)} &bull; {t.uploadedBy}
                </span>
              </div>
              <button
                type="button"
                className="artifact-file-delete"
                onClick={() => handleDelete(t.fileName)}
                title="Delete"
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── FreeTextSection ───────────────────────────────────────────────────────────
const FreeTextSection = ({ postMortemId, incidentId, isExpanded, onStatusChange }) => {
  const { accounts } = useMsal();
  const { getAllFreeTextFields, saveFreeTextFields, acquireFreeTextLock, releaseFreeTextLock, checkFreeTextLock } = FreeTextService();
  const userEmail = accounts[0]?.username || 'unknown';
  const [freeText, setFreeText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockedBy, setLockedBy] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const lockInterval = useRef(null);

  useEffect(() => {
    if (isExpanded) {
      loadFreeText();
      checkLock();
    } else {
      releaseLock();
    }
    return () => {
      if (lockInterval.current) clearInterval(lockInterval.current);
    };
  }, [isExpanded, postMortemId]);

  const loadFreeText = async () => {
    try {
      setLoading(true);
      const data = await getAllFreeTextFields(postMortemId);
      setFreeText(data?.freeTextFields?.['Additional Notes'] || '');
    } catch {
      setFreeText('');
    } finally {
      setLoading(false);
    }
  };

  const checkLock = async () => {
    try {
      const status = await checkFreeTextLock(postMortemId);
      if (status.isLocked) {
        setIsLocked(true);
        setLockedBy(status.lockedBy);
        setCanEdit(status.lockedBy === userEmail);
        if (status.lockedBy !== userEmail) {
          toast.warning(`${status.lockedBy} is currently editing. Please wait.`, { autoClose: 4000 });
        }
      } else {
        await tryAcquireLock();
      }
    } catch {
      setCanEdit(false);
    }
  };

  const tryAcquireLock = async () => {
    try {
      const result = await acquireFreeTextLock(postMortemId, userEmail);
      if (result.success) {
        setIsLocked(true);
        setLockedBy(userEmail);
        setCanEdit(true);
        if (lockInterval.current) clearInterval(lockInterval.current);
        lockInterval.current = setInterval(async () => {
          try {
            const r = await acquireFreeTextLock(postMortemId, userEmail);
            if (!r.success) {
              clearInterval(lockInterval.current);
              lockInterval.current = null;
              setCanEdit(false);
              toast.warning('Edit lock expired. Re-expand to edit.', { autoClose: 3000 });
            }
          } catch { }
        }, 4 * 60 * 1000);
      } else {
        setIsLocked(true);
        setLockedBy(result.lockedBy);
        setCanEdit(false);
        toast.warning(`${result.lockedBy} is currently editing. Please wait.`, { autoClose: 4000 });
      }
    } catch {
      setCanEdit(false);
    }
  };

  const releaseLock = async () => {
    try {
      if (canEdit && isLocked && lockedBy === userEmail) {
        await releaseFreeTextLock(postMortemId, userEmail);
        if (lockInterval.current) {
          clearInterval(lockInterval.current);
          lockInterval.current = null;
        }
        setCanEdit(false);
        setIsLocked(false);
      }
    } catch { }
  };

  const handleSave = async () => {
    if (!canEdit) { toast.error('No edit permission'); return; }
    try {
      setSaving(true);
      await saveFreeTextFields(postMortemId, incidentId, { 'Additional Notes': freeText || '' });
      toast.success('Saved successfully');
      onStatusChange?.('freetext', !!freeText);
      await releaseLock();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="artifact-section-body artifact-freetext-body">
      {loading ? (
        <div className="artifact-list-placeholder">Loading...</div>
      ) : (
        <>
          {isLocked && lockedBy !== userEmail && (
            <div className="artifact-lock-banner locked">
              🔒 <strong>{lockedBy}</strong> is currently editing. Please wait.
            </div>
          )}
          {canEdit && (
            <div className="artifact-lock-banner can-edit">
              ✓ You have editing access.
            </div>
          )}
          <TextArea
            name="artifacts-freetext"
            fit="medium"
            id={`artifacts-freetext-${postMortemId}`}
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder={canEdit ? 'Enter additional notes here...' : 'Locked for editing by another user'}
            disabled={saving || !canEdit}
            rows={8}
            style={{ width: '100%', fontFamily: 'inherit', fontSize: '14px', lineHeight: '1.6' }}
          />
          <div className="artifact-freetext-actions">
            <Button
              label={saving ? 'Saving...' : 'Save'}
              appearance="alt"
              fit="small"
              disabled={saving || loading || !canEdit}
              onClick={(e) => { e.preventDefault(); handleSave(e); }}
            />
          </div>
        </>
      )}
    </div>
  );
};

// ─── Section definitions ───────────────────────────────────────────────────────
const SECTIONS = [
  { key: 'transcript', label: 'Transcript' },
  { key: 'pir', label: '24 Hour Call Transcript' },
  { key: 'sre-review', label: 'PLATFORM Review Call Transcript' },
  { key: 'freetext', label: 'Free Text' },
];

// ─── ArtifactsPanel (main export) ─────────────────────────────────────────────
const ArtifactsPanel = forwardRef(({ postMortemId, incidentId, uploadedBy, hideTrigger }, ref) => {
  const { checkTranscriptExists, checkPirTranscriptExists, checkSreReviewTranscriptExists } = TranscriptService();
  const { getAllFreeTextFields } = FreeTextService();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasLoadedStatuses, setHasLoadedStatuses] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [statuses, setStatuses] = useState({
    transcript: false,
    pir: false,
    'sre-review': false,
    freetext: false,
  });

  const loadStatuses = useCallback(async () => {
    try {
      const [t, p, s, ft] = await Promise.all([
        checkTranscriptExists(postMortemId).catch(() => ({ hasTranscript: false })),
        checkPirTranscriptExists(postMortemId).catch(() => ({ hasTranscript: false })),
        checkSreReviewTranscriptExists(postMortemId).catch(() => ({ hasTranscript: false })),
        getAllFreeTextFields(postMortemId).catch(() => null),
      ]);
      setStatuses({
        transcript: !!t?.hasTranscript,
        pir: !!p?.hasTranscript,
        'sre-review': !!s?.hasTranscript,
        freetext: !!(ft?.freeTextFields?.['Additional Notes']),
      });
    } catch { }
  }, [postMortemId]);

  useEffect(() => {
    if (!isModalOpen || hasLoadedStatuses) return;
    loadStatuses();
    setHasLoadedStatuses(true);
  }, [isModalOpen, hasLoadedStatuses, loadStatuses]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpenModal,
  }));

  const handleCloseModal = () => {
    setExpandedSection(null);
    setIsModalOpen(false);
    loadStatuses();
  };

  const toggleSection = (key) => {
    setExpandedSection(prev => (prev === key ? null : key));
  };

  const handleStatusChange = (key, hasContent) => {
    setStatuses(prev => ({ ...prev, [key]: hasContent }));
  };

  return (
    <>
      {/* ── Trigger ─────────────────────────────────────────────────────────── */}
      {!hideTrigger && (
        <button
          type="button"
          className="artifacts-trigger-btn"
          onClick={handleOpenModal}
          title="Manage artifacts"
        >
          <span className="artifacts-btn-label">Upload Artifacts</span>
        </button>
      )}

      {/* ── Unified modal ───────────────────────────────────────────────────── */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        heading="Upload Artifacts"
        size="medium"
        actions={{
          secondaryAction: (
            <Button label="Close" variant="outlined" onClick={handleCloseModal} />
          ),
        }}
      >
        <div className="artifacts-modal-body">
          {SECTIONS.map(section => {
            const isExpanded = expandedSection === section.key;
            return (
              <div
                key={section.key}
                className={`accordion-section${isExpanded ? ' expanded' : ''}`}
              >
                <button
                  type="button"
                  className="accordion-header"
                  onClick={() => toggleSection(section.key)}
                  aria-expanded={isExpanded}
                >
                  <div className="accordion-header-left">
                    <span className={`accordion-status-dot${statuses[section.key] ? ' filled' : ''}`} />
                    <span className="accordion-title">{section.label}</span>
                  </div>
                  <span className="accordion-chevron">{isExpanded ? '▲' : '▼'}</span>
                </button>

                {isExpanded && (
                  <div className="accordion-body">
                    {section.key !== 'freetext' ? (
                      <TranscriptSection
                        postMortemId={postMortemId}
                        incidentId={incidentId}
                        uploadedBy={uploadedBy}
                        transcriptType={section.key}
                        isExpanded={true}
                        onStatusChange={handleStatusChange}
                      />
                    ) : (
                      <FreeTextSection
                        postMortemId={postMortemId}
                        incidentId={incidentId}
                        isExpanded={true}
                        onStatusChange={handleStatusChange}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
});

export default ArtifactsPanel;
